import { CATEGORIES, QUESTIONS } from '../data/questions';
import type { Category, MatchItem, MatchResult, Question } from '../types';

const PARTNER_A = 'Alex';
const PARTNER_B = 'Jamie';

type Intensity = 'open' | 'like' | 'love';

const INTENSITIES: Intensity[] = ['open', 'like', 'love'];
const SCORE: Record<Intensity, number> = { open: 1, like: 2, love: 3 };

function hash(id: string): number {
  let value = 0;

  for (let index = 0; index < id.length; index += 1) {
    value = ((value << 5) - value + id.charCodeAt(index)) | 0;
  }

  return Math.abs(value);
}

function buildMockMatchesByCategory(): Partial<Record<Category, MatchItem[]>> {
  const questionsById = new Map<string, Question>();
  const categories: Partial<Record<Category, MatchItem[]>> = {};
  const usedCounterparts = new Set<string>();

  for (const question of QUESTIONS) {
    questionsById.set(question.id, question);
  }

  for (const question of QUESTIONS) {
    if (usedCounterparts.has(question.id)) continue;

    const fingerprint = hash(question.id);

    // Roughly 53% of unique prompts become mock matches, which yields about 50% overall.
    if (fingerprint % 12 >= 7) continue;

    const intensityA = INTENSITIES[fingerprint % INTENSITIES.length]!;
    const intensityB = INTENSITIES[(fingerprint >> 4) % INTENSITIES.length]!;
    const matches = categories[question.category] ?? [];
    const tier = fingerprint % 5 < 2 ? 'free' : 'premium';

    if (question.counterpartId) {
      const counterpart = questionsById.get(question.counterpartId);

      if (counterpart) {
        usedCounterparts.add(counterpart.id);
        matches.push({
          questionId: question.id,
          questionText: question.text,
          counterpartQuestionText: counterpart.text,
          category: question.category,
          tier,
          intensityA,
          intensityB,
          matchScore: SCORE[intensityA] + SCORE[intensityB],
          matchType: 'counterpart',
        });
      }
    } else {
      matches.push({
        questionId: question.id,
        questionText: question.text,
        category: question.category,
        tier,
        intensityA,
        intensityB,
        matchScore: SCORE[intensityA] + SCORE[intensityB],
        matchType: 'same',
      });
    }

    categories[question.category] = matches;
  }

  for (const category of Object.keys(categories) as Category[]) {
    categories[category]?.sort((a, b) => b.matchScore - a.matchScore);
  }

  return categories;
}

const MOCK_MATCHES_BY_CATEGORY = buildMockMatchesByCategory();

export function buildMockMatchResult(isPaid: boolean): MatchResult {
  const categories = Object.fromEntries(
    CATEGORIES.map((category) => {
      const allMatches = MOCK_MATCHES_BY_CATEGORY[category.id] ?? [];
      const visibleMatches = isPaid
        ? allMatches
        : allMatches.filter((match) => match.tier === 'free');

      return [category.id, {
        totalMatches: allMatches.length,
        totalQuestions: QUESTIONS.filter((question) => question.category === category.id).length,
        matches: visibleMatches,
      }];
    }),
  );

  const totalMatches = Object.values(categories)
    .reduce((sum, category) => sum + category.totalMatches, 0);
  const visibleCount = Object.values(categories)
    .reduce((sum, category) => sum + category.matches.length, 0);

  return {
    sessionId: 'mock-session-001',
    isPaid,
    totalMatches,
    premiumMatchCount: totalMatches - visibleCount,
    partnerAName: PARTNER_A,
    partnerBName: PARTNER_B,
    categories,
  };
}
